import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Module-level singleton: only one SpeechRecognition can be active per page.
 * We hold the current recognizer here so any caller can stop it before
 * starting a new one (the Web Speech API spec enforces this).
 */
let activeRecognizer = null;

/**
 * Stop the active recognizer and invoke `callback` only once it has actually
 * ended. The Web Speech API tears a recognizer down asynchronously: building a
 * new one before the previous `onend` has fired makes the browser drop the new
 * session silently. The guided tour advances steps automatically, so stop/start
 * pairs happen back-to-back with no human pause in between — chaining through
 * `onend` is what keeps the mic reliably reopening on every step.
 */
function stopActive(callback) {
  if (activeRecognizer) {
    const rec = activeRecognizer;
    const prevOnEnd = rec.onend;
    rec.onend = () => {
      if (prevOnEnd) {
        try { prevOnEnd(); } catch { /* noop */ }
      }
      callback?.();
    };
    try {
      rec.stop();
    } catch {
      callback?.();
    }
    activeRecognizer = null;
  } else {
    callback?.();
  }
}

/**
 * Bumped on every stop() and every start(). Because `start` now resumes inside
 * an async callback, a session queued for one step could otherwise open the mic
 * after the tour has already moved on — with the previous step's language. Each
 * start captures the token and bails if it changed while it was waiting.
 */
let startToken = 0;

const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

export const isSpeechSupported = !!SR;

/** Lazily resolve the SpeechRecognition constructor (re-reads window at call time
 *  so tests can swap the global). Returns null if unsupported. */
function getSR() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * useSpeechRecognition — wraps the Web Speech API in a React hook.
 *
 * @param {object}   opts
 * @param {string}   opts.lang       BCP-47 language code (e.g. 'en-IN', 'hi-IN')
 * @param {boolean}  [opts.long]     If true, use continuous mode with silence
 *                                   auto-stop and a 60s hard cap (for long utterances)
 * @param {number}   [opts.silenceMs] How long a pause ends a `long` session.
 *                                   Defaults to 3s, which suits the short answers
 *                                   the guided tour and the registration form
 *                                   collect. Dictating clinical notes needs longer
 *                                   — a doctor pausing to think mid-sentence
 *                                   shouldn't close the mic — so those fields pass 5s.
 * @param {function} opts.onResult   Called with the final transcript when recognition ends
 * @param {function} [opts.onError]  Called with an error event
 * @param {function} [opts.onStatus] Called with a human-readable status string
 */
export function useSpeechRecognition({
  lang, long = false, silenceMs = 3000, onResult, onError, onStatus,
}) {
  const [isListening, setIsListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [elapsed, setElapsed] = useState(0);   // seconds, only meaningful in long mode

  // Refs hold values that change every recognition session but
  // shouldn't trigger the `start` callback to re-create.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onStatusRef = useRef(onStatus);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);

  const stop = useCallback(() => {
    startToken += 1;   // invalidate any start() still waiting on the old onend
    stopActive();
  }, []);

  const start = useCallback(() => {
    const Ctor = getSR();
    if (!Ctor) {
      onErrorRef.current?.({ error: 'not-supported' });
      return;
    }

    startToken += 1;
    const myToken = startToken;

    /** Build and launch the recognizer. Runs only once the previous one ended. */
    const begin = () => {
      const rec = new Ctor();
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = !!long;
      rec.maxAlternatives = 1;

      let finalTranscript = '';
      let lastInterim = '';   // remember the most recent interim; when rec.stop() is
                              // called (e.g. by the silence timer) the recognizer
                              // may fire onend WITHOUT finalizing the last interim,
                              // so we fall back to it.
      let silenceTimer = null;
      let hardStopTimer = null;
      let elapsedTimer = null;
      let elapsedSec = 0;

      const SILENCE_MS = silenceMs;
      const HARD_MAX_MS = 60000;

      rec.onstart = () => {
        activeRecognizer = rec;
        setIsListening(true);
        setInterim('');
        onStatusRef.current?.(long ? `Listening (long mode, ${lang})…` : `Listening (${lang})…`);

        if (long) {
          elapsedSec = 0;
          setElapsed(0);
          elapsedTimer = setInterval(() => {
            elapsedSec += 1;
            setElapsed(elapsedSec);
          }, 1000);
          hardStopTimer = setTimeout(() => rec.stop(), HARD_MAX_MS);
        }
      };

      rec.onresult = (event) => {
        let interimChunk = '';
        let localFinalTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          const tr = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            localFinalTranscript += tr + ' ';
          } else {
            interimChunk += tr;
          }
        }

        const newFinal = localFinalTranscript.trim();
        const finalChanged = newFinal !== finalTranscript;
        finalTranscript = newFinal;

        if (finalChanged) {
          lastInterim = '';
        }

        if (interimChunk.trim()) {
          lastInterim = interimChunk;
        }

        setInterim((finalTranscript + ' ' + lastInterim).trim());

        if (long) {
          clearTimeout(silenceTimer);
          silenceTimer = setTimeout(() => rec.stop(), SILENCE_MS);
        }
      };

      rec.onerror = (e) => {
        onErrorRef.current?.(e);
      };

      rec.onend = () => {
        clearTimeout(silenceTimer);
        clearTimeout(hardStopTimer);
        clearInterval(elapsedTimer);
        if (activeRecognizer === rec) activeRecognizer = null;

        // Deliver the transcript BEFORE flipping isListening, so a consumer
        // reacting to "listening finished" already sees the captured value.
        // This is a native callback, which React 17 does not batch, so the
        // opposite order gives the guided tour one render in which the mic has
        // closed but the answer hasn't landed — long enough to start a skip
        // countdown it then has to retract.
        //
        // When rec.stop() is called (e.g. by the silence timer), the recognizer
        // may fire onend WITHOUT finalizing the last interim result. Fall back
        // to lastInterim so the user's speech isn't dropped on the floor.
        const transcript = (finalTranscript + ' ' + lastInterim).trim();
        if (transcript) {
          onResultRef.current?.(transcript, { elapsedSec, lang });
        } else {
          onStatusRef.current?.('No speech detected.');
        }

        setIsListening(false);
        setInterim('');
        setElapsed(0);
      };

      try {
        rec.start();
      } catch (e) {
        onErrorRef.current?.({ error: 'start-failed', message: e.message });
      }
    };

    // spec: only one recognizer at a time — wait for the previous one to end
    stopActive(() => {
      // A newer start() (or a stop()) happened while we were waiting; that call
      // owns the mic now, so abandon this session rather than stealing it.
      if (myToken !== startToken) return;
      begin();
    });
  }, [lang, long, silenceMs]);

  // Cleanup on unmount
  useEffect(() => stop, [stop]);

  return { start, stop, isListening, interim, elapsed };
}
